/* eslint-disable no-unused-vars */
import axios from 'axios';
import $ from '@nuskin/nuskinjquery';
import { UrlService, storage, events, RunConfigService, equinoxLocalStorage } from '@nuskin/ns-util';
import { getConfiguration, getCachedConfiguration } from '@nuskin/configuration-sdk';
import { ProductDataService } from "@nuskin/ns-product";
import { Product } from "@nuskin/ns-product-lib";

const defaultHeaders = {
    'Accept': '*',
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('equinox-okta-token') ? localStorage.getItem('equinox-okta-token') : ''}`
}

const shoppingContextCartTypes = {
    'storefront': 'MSCART',
    'personal_offer': 'POCART'
}

const DEFAULT_CART_TYPE = 'USER';

export default {
    addProductToEquinoxCart,
    getEquinoxCart,
    addBundleToEquinoxCart,
    getEquinoxRequestConfiguration,
    _getEquinoxSession,
    _getCartShoppingContext,
    _equinoxRequest,
    goToCartPage,
    _assembleChildSkus,
    _assembleAdrProperties
}

/**
 * _getEquinoxSession - fetches / generated an equinox session
 * @returns { Promise<axios> }
 */
function _getEquinoxSession() {

    const equinoxRequestOptions = {
        method: 'GET',
        endpoint: 'customers'
    }

    return _equinoxRequest(equinoxRequestOptions);
}

/**
 * addProductToEquinoxCart - Sends a request to the Equinox API to add a product to the cart.
 *
 * @async
 * @param {Object} options - An object containing options for the cart item.
 * @param {number} options.qty - Required. The quantity of the product to add to the cart.
 * @param {Object} options.product - Required. An object representing the product to be added to the cart.
 * @param {string} options.sku - Required. The SKU of the product to be added.
 * @param {string} options.product.equinoxProductId - Required. The Equinox product ID of the product to be added.
 * @param {Object} [options.product.properties] - Optional. An object with additional properties for the product to be added to the cart.
 * @param {string} [options.product.properties.title] - Optional. The title of the product to be added. If not provided, `options.product.title` will be used.
 * @param {string} [options.product.properties.slug] - Optional. The slug of the product to be added. If not provided, a slug will be generated based on `options.product.title`.
 * @param {Array<Object>} [options.product.childSkus] - Optional. An array of child SKUs if the product has children (kit or subcription).
 *                          productId: string - The Equinox product ID of the child product.
 *                          skuId: string - The SKU of the child product.
 *                          type: string - The type of the child product.
 *                          availableChannels: Array - An array of available channels for the child product.
 *                          inventory: Object - An object containing inventory information for the child product.
 *                              backOrdered: boolean - A boolean indicating if the child product is back-ordered.
 *                              skuQuantity: number - The quantity of the child product in stock.
 * @param {boolean} [options.adr=false] - Optional. A boolean indicating if the product is on Auto Delivery.
 * @param {Object} [options.subscription] - Optional. An object containing subscription options if the product is on Auto Delivery.
 * @param {string} [options.subscription.frequencyPeriod="1"] - Optional. The frequency period of the subscription.
 * @param {string} [options.subscription.frequencyType="MONTHS"] - Optional. The frequency type of the subscription.
 *
 * @returns {Promise<AxiosResponse>} A promise that resolves with the response from the Equinox API.
 */
async function addProductToEquinoxCart(options) {
    const cartType = _getCartShoppingContext();

    let requestData = {
        "quantity": options.qty,
        "skus": [{
            "skuId": options.product.sku,
            "productId": options.product.equinoxProductId,
            "type": "DEFAULT"
        }],
        "properties": options.product.properties ? options.product.properties : { "categoryId": "antiaging" }
    };

    requestData = _assembleChildSkus(requestData, options)
    requestData = _assembleAdrProperties(requestData, options)

    if (options.product.inventory === "BACKORDER") {
        requestData.properties = { ...requestData.properties, isBackOrdered: true }
    }

    //check all skus
    if (requestData.skus.some(item => item.backOrdered)) {
        requestData.properties = { ...requestData.properties, isBackOrdered: true };
    }

    const equinoxRequestOptions = {
        method: 'POST',
        endpoint: `carts/${cartType}/items`,
        data: [requestData]
    }

    return await _equinoxRequest(equinoxRequestOptions);
}

/**
 * Adds a bundle of products to the user's Equinox cart.
 *
 * @async
 * @function addBundleToEquinoxCart
 * @param {Array} [productBundleSkus=[]] - An array of product SKU IDs that are included in the bundle to be added to the cart.
 * @returns {Promise} A Promise that resolves with the result of the Equinox request.
 *
 * @throws {Error} If the Equinox request fails.
 */
async function addBundleToEquinoxCart(productBundleSkus = []) {

    const cartType = _getCartShoppingContext();
    const productsInBundle = await ProductDataService.getProductData(productBundleSkus);
    const mappedProductBundles = productsInBundle.products.map((productBundle) => {
        return new Product(productBundle);
    })

    const bundleItems = [];

    mappedProductBundles.forEach((productItem) => {
        let skuToAdd = productItem.sku;
        if (productItem) {

            let bundleItem = {
                "quantity": 1, //TODO clarify if there are bundles that has > 1 qty of item of type
                "skus": [
                    {
                        "skuId": skuToAdd,
                        "productId": productItem.equinoxProductId,
                        "type": "DEFAULT"
                    }
                ],
                "properties": productItem.properties ? productItem.properties : { "categoryId": "antiaging" }
            }

            bundleItem = _assembleChildSkus(bundleItem, { product: productItem });
            bundleItem = _assembleAdrProperties(bundleItem, { product: productItem });

            if (productItem.inventory === "BACKORDER") {
                bundleItem.properties = { ...bundleItem.properties, isBackOrdered: true };
            }

            //check all skus
            if (bundleItem.skus.some(item => item.backOrdered)) {
                bundleItem.properties = { ...bundleItem.properties, isBackOrdered: true };
            }

            bundleItems.push(bundleItem);
        }
    })

    const options = {
        method: 'POST',
        endpoint: `carts/${cartType}/items`,
        data: bundleItems
    }

    return await _equinoxRequest(options);

}

/**
 * _getCartShoppingContext - set cart type based on storage shopping context
 * @returns { String }
 */
function _getCartShoppingContext() {
    let cartType = DEFAULT_CART_TYPE;

    const shoppingContext = storage.getItem(storage.keys.SHOPPING_CONTEXT);

    if (shoppingContext && shoppingContextCartTypes[shoppingContext.context]) {
        cartType = shoppingContextCartTypes[shoppingContext.context];
    }

    return cartType;
}

/**
 * getEquinoxCart - fetch equinox cart
 * @returns { Promise<axios> }
 */
async function getEquinoxCart() {

    const cartType = _getCartShoppingContext();

    const equinoxRequestOptions = {
        method: 'GET',
        endpoint: `carts/${cartType}`
    }

    return await _equinoxRequest(equinoxRequestOptions);

}

/**
 * getEquinoxRequestConfiguration - fetch equinox market and kong url
 * @returns { Object }
 */
async function getEquinoxRequestConfiguration() {
    const cartType = _getCartShoppingContext();
    const marketLocale = UrlService.getLocale();
    const [, market] = marketLocale.split("_");
    const config = (await getConfiguration(['Equinox_Markets'])).Equinox_Markets;

    // Use MySite_API_Base_URLs if it is in MySite or MSCART
    const mySiteKongURL = (cartType === shoppingContextCartTypes.storefront || window.location.hostname.includes(".mynuskin.com")) ? config.MySite_API_Base_URLs : config.API_Base_URLs;

    const equinoxMarket = {
        market_name: config.market_name,
        country_code: config.country_code,
        store_id: config.store_id
    };

    return { equinoxMarket, mySiteKongURL, marketLocale, market }
}

//TODO transfer _equinoxRequest into a library
/**
 * _equinoxRequest - equinox axios facade
 * @param { Array } options - [{
 *              method: http method,
 *              endpoint: the equinox api endpoint
 *              params: get params
 *              data: post data
 *          }]
 * @returns { Object }
 */
async function _equinoxRequest(options) {
    const { equinoxMarket, mySiteKongURL, marketLocale } = await getEquinoxRequestConfiguration();
    const defaultEquinoxParams = {
        locale: marketLocale,
        storeId: equinoxMarket.store_id
    }

    const oktaTokens = sessionStorage.getItem("oktaTokens");

    if (oktaTokens) {
        const { accessToken } = JSON.parse(oktaTokens);
        defaultHeaders.Authorization = `Bearer ${accessToken}`;
    }

    let axiosRequest = {
        method: options.method,
        url: `${mySiteKongURL}/orchestrationservices/storefront/${options.endpoint}`,
        headers: defaultHeaders,
        withCredentials: (options.method !== "DELETE"),
        params: (options.params) ? options.params : defaultEquinoxParams
    };

    if (options.data) {
        axiosRequest.data = options.data
    }


    return axios(axiosRequest)

}

/**
 * Navigates the user to the shopping cart page on the Nu Skin website or storefront website if context is storefront.
 *
 * @function
 * @name goToCartPage
 * @returns {void}
 */
function goToCartPage() {

    const [locale, market] = UrlService.getLocale().split("_");
    const currentEnvironment = RunConfigService.getEnvironmentCode();
    const env = currentEnvironment !== 'prod' ? 'test.' : '';
    const shoppingContext = storage.getItem(storage.keys.SHOPPING_CONTEXT);

    let url = `https://${env}nuskin.com/${market.toLowerCase()}/${locale.toLowerCase()}/viewCart`;

    if (shoppingContext && shoppingContext.context === "storefront") {
        url = `https://mysite.${env}mynuskin.com/${market.toLowerCase()}/${locale.toLowerCase()}/viewCart`;
    }

    window.location = url;
}

/**
 * Assembles the child SKUs for a given product and adds them to the request data.
 *
 * @private
 * @function _assembleChildSkus
 * @param {Object} requestData - The request data object that contains the item details.
 * @param {Object} options - The options object that contains the product details.
 * @returns {Object} The request data object with the child SKUs added.
 */
function _assembleChildSkus(requestData, options) {
    const productSku = options.sku
    let productWithVariant = []
    let personalOffer = sessionStorage.getItem('personalOffer');
    console.log(personalOffer)
    if (personalOffer !== null) {
        personalOffer = JSON.parse(personalOffer);
        productWithVariant = personalOffer.products.filter((sku) =>
            sku.type === 'bundle' && sku.variantSelected !== null && sku.sku === productSku
        )
    }

    const config = getCachedConfiguration('Equinox_Markets');

    if (options.product.childSkus
        && Array.isArray(options.product.childSkus)
        && options.product.childSkus.length > 0) {
        requestData.skus = options.product.childSkus.map((cs) => {
            const variantSelectedId = (personalOffer != null && productWithVariant.length > 0) ? Object.keys(productWithVariant[0].variantSelected)[0] : null
            const childSku = (personalOffer != null && variantSelectedId !== null && variantSelectedId === cs.productId && config.MySite_graphql_active) ? {
                productId: cs.productId,
                skuId: productWithVariant[0].variantSelected[variantSelectedId],
                type: cs.type,
                availableChannels: cs.availableChannels
            } : {
                productId: cs.productId,
                skuId: cs.skuId,
                type: cs.type,
                availableChannels: cs.availableChannels
            }
            if (cs.inventory) {
                childSku.skuQuantity = cs.skuQuantity;
                childSku.backOrdered = cs.inventory.backOrdered;
            }

            return childSku;
        });
    }

    return requestData;
}

/**
 * Assemble ADR properties and subscription data into the requestData object.
 *
 * @private
 * @function _assembleAdrProperties
 * @param {Object} requestData - The requestData object to be modified.
 * @param {Object} options - The options object containing ADR data.
 * @param {Boolean} options.adr - Whether or not this is an ADR order.
 * @param {Object} options.subscription - The subscription data for the ADR order.
 * @param {Object} options.product - The product data for the ADR order.
 * @returns {Object} The modified requestData object.
 */
function _assembleAdrProperties(requestData, options) {

    if (options.adr || (options.product.hasOwnProperty("isAdrOnly") && options.product.isAdrOnly())) {
        requestData.subscription = options.subscription ? options.subscription : { frequencyPeriod: "1", frequencyType: "MONTHS" }
        requestData.isSubscription = true;

        requestData.properties = {
            categoryId: options.product.productCategory || 'all_products',
            slug: options.product.slug ? options.product.slug : options.product.title.toLowerCase().replace(" ", "-"),
            scanQualifiedCount: options.product.scanQualified || 0,
            isBackOrdered: requestData.skus && requestData.skus[requestData.skus.length - 1].backOrdered || false,
            imageURL: options.product.fullImage || '',
            name: options.product.title,
            productId: options.product.equinoxProductId || ''
        };
    }

    return requestData;

}

const runConfigCallback = async (runConfig) => {
    if (runConfig && typeof runConfig.clientId === 'string') {
        const equinoxMarketConfig = (await getConfiguration(["Equinox_Markets"])).Equinox_Markets;

        const publishEquinoxCartCount = (options) => {
            getEquinoxCart().then((response) => {
                const cart = response.data;
                events.publish(events.shop.CART_UPDATED, { cartInfo: { qty: cart.value.count }, ...options });
            });
        }

        if (equinoxLocalStorage.isEquinoxEnabled() && equinoxMarketConfig.active) {
            _getEquinoxSession();
            events.subscribe(events.shop.ADD_TO_CART, (options) => {
                publishEquinoxCartCount(options)
            });

            events.subscribe(events.shop.SET_SHOPPING_CONTEXT, () => {
                publishEquinoxCartCount()
            });
        }
    }
}

events.getValue(events.global.RUN_CONFIG_CHANGED, runConfigCallback, true);